<template>
  <div class="surface">
    <div v-if="!hideHead" class="sHead">
      <span class="sTitle">{{ isTeam ? 'Verified callback default' : 'Your verified callback number' }}</span>
      <p class="sCopy">{{ copy }}</p>
    </div>

    <!-- Active call-and-code -->
    <template v-if="isActive">
      <CallAndCodePanel
        :phase="verifyPhase"
        :phone-number="telephony.verify.phoneNumber"
        :code="telephony.verify.code"
        :seconds-left="telephony.verify.secondsLeft"
        :fail-reason="telephony.verify.failReason"
        @cancel="onCancel"
        @recall="onRecall"
      />
      <button
        v-if="verifyPhase === 'verified'"
        type="button"
        class="btn primary doneBtn"
        @click="onDone"
      >
        Done
      </button>
    </template>

    <!-- Form / current status -->
    <template v-else>
      <div v-if="currentNumber" class="currentCard">
        <div class="currentMain">
          <span class="currentLabel">{{ currentLabel }}</span>
          <span class="currentNum">{{ prettyCurrent }}</span>
        </div>
        <StatusChip :tone="currentVerified ? 'success' : 'amber'" :label="currentVerified ? '✓ Verified' : 'Unverified'" />
      </div>
      <button v-if="collapsed" type="button" class="changeBtn" @click="changing = true">Change number</button>

      <template v-if="!collapsed">
      <!-- Team default: pick from numbers reps have already verified, instead of
           re-verifying. Grows as reps verify their own callback numbers. -->
      <template v-if="isTeam && callerIds.length">
        <div class="pick">
          <FormField label="Choose an already-verified number" :error="pickError">
            <select v-model="picked" class="tinput">
              <option value="">Select a number…</option>
              <option v-for="c in callerIds" :key="c.phone_number" :value="c.phone_number">
                {{ optionLabel(c) }}
              </option>
            </select>
          </FormField>
          <button type="button" class="btn primary pickBtn" :disabled="!picked || settingDefault" @click="onSetDefault">
            Set as default
          </button>
        </div>
        <div class="orRow"><span class="orLabel">or verify a new number</span></div>
      </template>

      <div class="form">
        <FormField label="Your phone number" :error="phoneError">
          <PhoneInput v-model="phone" placeholder="+1 (628) 555-0144" @input="phoneError = ''" />
        </FormField>
        <FormField v-if="!isTeam" label="Label" optional>
          <input v-model="label" class="tinput" type="text" placeholder="My mobile" />
        </FormField>
        <button type="button" class="btn primary verifyBtn" @click="onVerify">
          <AppIcon name="phone_call" :size="14" />{{ currentNumber ? 'Verify a new number' : 'Verify my number' }}
        </button>
        <p class="hint">
          We’ll call this number to confirm it’s yours — just enter the code we show you on your
          phone’s keypad when you pick up.
        </p>
      </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import AppIcon from '../../../../components/AppIcon.vue'
import FormField from './FormField.vue'
import StatusChip from './StatusChip.vue'
import CallAndCodePanel from './CallAndCodePanel.vue'
import PhoneInput from './PhoneInput.vue'
import { useTelephony } from '../../../composables/useTelephony'
import { formatPhone, toE164 } from '../../../utils/phone'
import { INVALID_PHONE } from './telephonyValidation'
import { getTelephonyCallerIds, setCallbackFallback, type VerifiedCallerId } from '../../../../services/dialerApi'

const props = withDefaults(
  defineProps<{ variant: 'own' | 'team_default'; hideHead?: boolean }>(),
  { hideHead: false },
)

const telephony = useTelephony()
const isTeam = computed(() => props.variant === 'team_default')
const target = computed<'own' | 'team'>(() => (isTeam.value ? 'team' : 'own'))

const phone = ref('')
const label = ref('')
const phoneError = ref('')

// Team-default only: pick from numbers already verified across the team.
const callerIds = ref<VerifiedCallerId[]>([])
const picked = ref('')
const settingDefault = ref(false)
const pickError = ref('')

const verifyPhase = computed(() => telephony.verify.phase as 'calling' | 'waiting' | 'failed' | 'verified')
const isActive = computed(
  () => telephony.verify.phase !== 'idle' && telephony.verify.target === target.value,
)

const copy = computed(() =>
  isTeam.value
    ? 'When a prospect calls back, we ring the rep who reached out. If we can’t tell who that was, we ring this number — so nothing slips through.'
    : 'You call prospects from local numbers. When someone calls one back, we ring this number so it reaches you.',
)

const me = computed(() => telephony.projection.value?.me ?? null)
const teamDefault = computed(() => telephony.projection.value?.callback_default ?? null)

const currentNumber = computed(() =>
  isTeam.value ? teamDefault.value?.phone_number || '' : me.value?.callback_number?.phone_number || '',
)
const currentVerified = computed(() =>
  isTeam.value ? !!teamDefault.value?.verified : me.value?.callback_number?.status === 'verified',
)
const currentLabel = computed(() =>
  isTeam.value ? 'Team default' : me.value?.callback_number?.label || 'Callback number',
)
const prettyCurrent = computed(() => formatPhone(currentNumber.value) || currentNumber.value)

// Team default: once a verified number is set, the pick/verify form hides behind "Change number".
const changing = ref(false)
const collapsed = computed(() => isTeam.value && !!currentNumber.value && currentVerified.value && !changing.value)
watch(currentNumber, () => { changing.value = false })

function onVerify() {
  const num = toE164(phone.value)
  if (!num) {
    phoneError.value = INVALID_PHONE
    return
  }
  void telephony.startCallbackVerification({
    phone_number: num,
    label: label.value.trim() || undefined,
    target: target.value,
  })
}

function onCancel() {
  void telephony.cancelVerification(target.value)
}
function onRecall() {
  void telephony.recall(target.value)
}
function onDone() {
  changing.value = false
  telephony.resetVerify()
  phone.value = ''
  label.value = ''
}

function optionLabel(c: VerifiedCallerId): string {
  const num = formatPhone(c.phone_number) || c.phone_number
  return c.friendly_name ? `${c.friendly_name} · ${num}` : num
}

async function onSetDefault() {
  if (!picked.value) return
  settingDefault.value = true
  pickError.value = ''
  try {
    await setCallbackFallback(picked.value)
    await telephony.refresh()
    picked.value = ''
  } catch {
    pickError.value = 'Couldn’t set that number — try again.'
  } finally {
    settingDefault.value = false
  }
}

onMounted(async () => {
  if (!isTeam.value) return
  try {
    const res = await getTelephonyCallerIds()
    callerIds.value = res.caller_ids || []
  } catch {
    callerIds.value = []
  }
})
</script>

<style scoped>
.surface { display: flex; flex-direction: column; gap: 18px; width: 100%; max-width: 520px; }
.sHead { display: flex; flex-direction: column; gap: 8px; }
.sTitle { font-family: var(--font-display); font-size: 22px; font-weight: var(--fw-semibold); letter-spacing: -0.01em; }
.sCopy { margin: 0; font-size: var(--fs-sm); line-height: var(--lh-loose); color: var(--color-text-secondary); }

.currentCard {
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-3);
  background: var(--color-bg-surface); border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg); padding: 14px 16px;
}
.currentMain { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.currentLabel { font-size: var(--fs-sm); font-weight: var(--fw-semibold); }
.currentNum { font-family: var(--font-mono); font-size: var(--fs-sm); color: var(--color-text-secondary); }

.changeBtn {
  align-self: flex-start; border: 0; background: none; padding: 0; cursor: pointer;
  font-size: var(--fs-sm); font-weight: var(--fw-medium); color: var(--color-accent);
}
.changeBtn:hover { text-decoration: underline; }

.form { display: flex; flex-direction: column; gap: var(--space-4); }
.verifyBtn { align-self: flex-start; padding: var(--space-2) var(--space-5); }
.doneBtn { align-self: flex-start; padding: var(--space-2) var(--space-6); }
.hint { margin: 0; font-size: var(--fs-xs); line-height: var(--lh-normal); color: var(--color-text-tertiary); }

.pick { display: flex; flex-direction: column; gap: var(--space-3); }
.pickBtn { align-self: flex-start; padding: var(--space-2) var(--space-5); }
.orRow { display: flex; align-items: center; gap: var(--space-3); color: var(--color-text-tertiary); }
.orRow::before, .orRow::after { content: ''; flex: 1; height: 1px; background: var(--color-border-subtle); }
.orLabel { font-size: var(--fs-xs); white-space: nowrap; }
</style>
